import type { JSX, ReactNode } from 'react'
import cx from 'classnames'
import { type AvatarSubject, Avatar } from '@/components/Avatar'
import { RawButton } from '@/components/Button'
import { Icon, type IconName } from '@/components/Icon'
import { Tooltip } from '@/components/Tooltip'
import { UnreadBadge } from '@/components/UnreadBadge'
import { links } from '@/utils/link'
import { NavLink } from 'react-router'
import styles from './Navigation.module.css'
import { IconButton } from '@/components/IconButton'

export interface NavigationPage {
  name: string
  path: string
  hidden: boolean
  exact?: boolean
  icon: JSX.Element
  title: string
  badgeCount?: number
}

export interface NavigationProps {
  floating: boolean
  children?: ReactNode
  logoIconWhite: string
  pages: NavigationPage[]
  canDraftJob: boolean
  onNewJob: () => unknown
  canSwitchWorkspaces: boolean
  isWorkspaceSelectorOpen: boolean
  onToggleWorkspaceSelector: () => unknown
  notificationCount: number
  onToggleNotifications: () => unknown
  accountAvatar: AvatarSubject
  onAccountPress: () => unknown
  profileDropdown?: ReactNode
  notificationsSidebar?: ReactNode
}

export function Navigation({
  children,
  floating,
  logoIconWhite,
  pages,
  canDraftJob,
  onNewJob,
  canSwitchWorkspaces,
  isWorkspaceSelectorOpen,
  onToggleWorkspaceSelector,
  notificationCount,
  onToggleNotifications,
  accountAvatar,
  onAccountPress,
  profileDropdown,
  notificationsSidebar,
}: NavigationProps) {
  return (
    <div
      className={cx(styles.container, floating && styles.floating)}
      data-test="RootNavigation"
    >
      <div className={styles.navigation}>
        <div
          className={styles.logo}
          title={`Worklayer Portal v${process.env.__VERSION__}`}
        >
          <a
            href={links.welcome()}
            className={styles.logoAnchor}
            data-test="Logo"
          >
            <img className={styles.logoImg} src={logoIconWhite} alt="Logo" />
          </a>
        </div>
        <div className={styles.separator} />
        <div className={styles.pages}>
          {pages
            .filter((page) => !page.hidden)
            .map((page) => (
              <Tooltip content={page.title} key={page.name}>
                <div className={styles.pageLink}>
                  <NavLink
                    className={({ isActive }) =>
                      cx(styles.button, isActive && styles.active)
                    }
                    to={page.path}
                    end={page.exact}
                    data-test={page.name}
                  >
                    <span className={styles.icon}>{page.icon}</span>
                    {!!page.badgeCount && (
                      <div className={styles.unread}>
                        <UnreadBadge count={page.badgeCount} />
                      </div>
                    )}
                  </NavLink>
                </div>
              </Tooltip>
            ))}

          {canDraftJob && (
            <NewJobNavLink
              onPress={onNewJob}
              name="create-new-job-button"
              title="New job"
              icon="add"
            />
          )}
        </div>
        <div className={styles.account}>
          <div className={styles.workspaces}>
            {canSwitchWorkspaces && (
              <Tooltip
                content={
                  isWorkspaceSelectorOpen
                    ? 'Close Workspace Switcher'
                    : 'Open Workspace Switcher'
                }
              >
                <RawButton
                  onPress={onToggleWorkspaceSelector}
                  testId="WorkspaceSelectorToggle"
                  className={cx(
                    styles.workspacesButton,
                    isWorkspaceSelectorOpen && styles.active,
                  )}
                >
                  <Icon name="switch-workspaces" />
                </RawButton>
              </Tooltip>
            )}
          </div>
          <div className={styles.notifications}>
            <Tooltip content="Notifications">
              <RawButton
                className={styles.notificationsButton}
                testId="NotificationsToggle"
                onPress={(e) => {
                  e.preventDefault()
                  return onToggleNotifications()
                }}
              >
                <Icon name="notifications" />
                {notificationCount > 0 && (
                  <div className={styles.unread}>
                    <UnreadBadge count={notificationCount} />
                  </div>
                )}
              </RawButton>
            </Tooltip>
          </div>
          <div className={styles.profile}>
            <div className={styles.avatarContainer}>
              <RawButton
                className={styles.profileButton}
                onPress={(e) => {
                  e.preventDefault()
                  return onAccountPress()
                }}
                testId="ProfileButton"
              >
                <Avatar size={30} subject={accountAvatar} />
              </RawButton>
              {profileDropdown}
            </div>
          </div>
        </div>
      </div>
      <div className={styles.children}>{children}</div>
      {notificationsSidebar}
    </div>
  )
}

interface NewJobNavLinkProps {
  onPress: () => unknown
  name: string
  title: string
  icon: IconName
}

function NewJobNavLink({ onPress, name, title, icon }: NewJobNavLinkProps) {
  return (
    <Tooltip content={title}>
      <IconButton
        name={icon}
        label={title}
        className={styles.button}
        onPress={onPress}
        testId={name}
      />
    </Tooltip>
  )
}
